import { PageShell } from "@/components/layout/PageShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getPriorityStatusMeta,
  priorityTypeBadgeClass,
} from "@/components/priorities/priorityCardTokens";
import { PriorityDetailDialog } from "@/components/priorities/PriorityDetailDialog";
import { PromoteQueueToFocusDialog } from "@/components/priorities/promote-queue-to-focus-dialog";
import { PriorityStatusTooltip } from "@/components/priorities/PriorityStatusTooltip";
import { PppChips } from "@/components/priorities/PppChips";
import { bestPpp } from "@/components/priorities/group-by-ppp";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  getAssignmentLimitMessage,
  getFocusUpdateRequiredMessage,
  getPriorityAuthzMessage,
} from "@/lib/priority-errors";
import { cn, getCurrentWeek, getISOWeek } from "@/lib/utils";
import { useMyPriorities } from "@/hooks/useMyPriorities";
import { priorityService } from "@/services/priorityService";
import { reportService } from "@/services/reportService";
import { Priority, PriorityStatus, Report } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  Flame,
  Inbox,
  Loader2,
  Star,
  TrendingUp,
  UserCheck,
  Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const LAST_REPORT_DAYS = 7;
const RECENT_REPORT_LIMIT = 100; // assumes <100 reports per ~7-day window for streak accuracy
const HOLDING_STATUSES: PriorityStatus[] = ["on-hold", "waiting", "blocked"];

function getUserAssignment(priority: Priority | null | undefined, userPid?: string) {
  if (!priority || !userPid) return undefined;
  return priority.assignments?.find((assignment) => assignment.assignedUser.pid === userPid);
}

function getPersonalStatus(priority: Priority | null | undefined, userPid?: string): PriorityStatus {
  return (getUserAssignment(priority, userPid)?.status ?? priority?.status ?? "queue") as PriorityStatus;
}

function getRelativeReportDate(report?: Report) {
  if (!report) return "";

  const date = new Date(report.reportDate || report.createdAt);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const reportDay = new Date(date);
  reportDay.setHours(0, 0, 0, 0);

  const diffDays = Math.round((today.getTime() - reportDay.getTime()) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(date);
}

function startOfLocalDay(date: Date) {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
}

function getDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getReportDate(report: Report) {
  return startOfLocalDay(new Date(report.reportDate || report.createdAt));
}

function getLastReportDays() {
  return Array.from({ length: LAST_REPORT_DAYS }, (_, index) => {
    const date = startOfLocalDay(new Date());
    date.setDate(date.getDate() - (LAST_REPORT_DAYS - 1 - index));
    return {
      key: getDateKey(date),
      date,
      label: new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(date),
    };
  });
}

function confidenceForStatus(status: PriorityStatus) {
  if (status === "blocked") {
    return {
      label: "Off track",
      Icon: AlertCircle,
      className: "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
    };
  }

  if (status === "waiting" || status === "on-hold") {
    return {
      label: "At risk",
      Icon: Clock3,
      className:
        "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
    };
  }

  return {
    label: status === "done" ? "Completed" : "On track",
    Icon: CheckCircle2,
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  };
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 mt-7 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function LoadingCard() {
  return (
    <Card className="rounded-lg border-border/70">
      <CardContent className="p-5">
        <div className="space-y-4">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-8 w-2/3" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <Skeleton className="h-10 rounded-md" />
            <Skeleton className="h-10 rounded-md" />
            <Skeleton className="h-10 rounded-md" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PriorityMetaBadges({ priority, userPid }: { priority: Priority; userPid?: string }) {
  const personalStatus = getPersonalStatus(priority, userPid);
  const statusMeta = getPriorityStatusMeta(personalStatus);
  const StatusIcon = statusMeta.icon;
  const dri = priority.assignments?.find((assignment) => assignment.isDri)?.assignedUser ?? priority.dri;
  const collaborators =
    priority.assignments
      ?.filter((assignment) => assignment.assignedUser.pid !== userPid)
      .map((assignment) => assignment.assignedUser.fullName) ?? [];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PriorityStatusTooltip status={personalStatus}>
        <Badge variant="outline" className={cn("h-6 gap-1.5 px-2.5 text-[11px] font-semibold", statusMeta.className)}>
          <StatusIcon className="h-3 w-3" />
          {statusMeta.label}
        </Badge>
      </PriorityStatusTooltip>
      <Badge
        variant="outline"
        className={cn("h-6 rounded px-2 text-[10px] font-bold uppercase tracking-wide", priorityTypeBadgeClass[priority.type])}
      >
        {priority.type}
      </Badge>
      <PppChips ppps={priority.ppps} isCandidate={priority.isCandidate} max={2} />
      {dri ? (
        <Badge variant="outline" className="h-6 gap-1.5 rounded-full border-amber-200 bg-amber-50 px-2.5 text-[11px] text-amber-700">
          <UserCheck className="h-3 w-3" />
          {dri.pid === userPid ? "You own it" : dri.fullName}
        </Badge>
      ) : null}
      {collaborators.length > 0 ? (
        <Badge variant="outline" className="h-6 rounded-full bg-muted/40 px-2.5 text-[11px] text-muted-foreground">
          + {collaborators.slice(0, 2).join(", ")}
        </Badge>
      ) : null}
    </div>
  );
}

function QueueRow({
  priority,
  index,
  userPid,
  onSetFocus,
  onOpen,
  isBusy,
}: {
  priority: Priority;
  index: number;
  userPid?: string;
  onSetFocus: (priority: Priority) => void;
  onOpen: (priority: Priority) => void;
  isBusy: boolean;
}) {
  const personalStatus = getPersonalStatus(priority, userPid);
  const statusMeta = getPriorityStatusMeta(personalStatus);
  const StatusIcon = statusMeta.icon;

  return (
    <div
      onClick={() => onOpen(priority)}
      className="grid cursor-pointer grid-cols-[2rem_1fr_auto] items-center gap-3 border-b border-border/60 py-3 transition-colors last:border-b-0 hover:bg-muted/40"
    >
      <div className="flex h-7 w-7 items-center justify-center rounded-md border bg-muted text-xs font-semibold text-muted-foreground">
        {index + 1}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground">{priority.name}</div>
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono">{priority.code}</span>
          {bestPpp(priority) ? <span className="truncate">{bestPpp(priority)!.name}</span> : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          disabled={isBusy}
          onClick={() => onSetFocus(priority)}
        >
          <Star className="h-3.5 w-3.5" />
          Set as focus
        </Button>
        <PriorityStatusTooltip status={personalStatus}>
          <Badge variant="outline" className={cn("h-6 gap-1.5 px-2 text-[11px] font-semibold", statusMeta.className)}>
            <StatusIcon className="h-3 w-3" />
            {statusMeta.label}
          </Badge>
        </PriorityStatusTooltip>
      </div>
    </div>
  );
}

function CandidateRow({
  priority,
  userPid,
  onPromote,
  onOpen,
  isBusy,
}: {
  priority: Priority;
  userPid?: string;
  onPromote: (priority: Priority) => void;
  onOpen: (priority: Priority) => void;
  isBusy: boolean;
}) {
  const personalStatus = getPersonalStatus(priority, userPid);
  const statusMeta = getPriorityStatusMeta(personalStatus);
  const StatusIcon = statusMeta.icon;

  return (
    <div
      onClick={() => onOpen(priority)}
      className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 border-b border-border/60 py-3 transition-colors last:border-b-0 hover:bg-muted/40"
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-foreground">{priority.name}</div>
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono">{priority.code}</span>
          {bestPpp(priority) ? <span className="truncate">{bestPpp(priority)!.name}</span> : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <PriorityStatusTooltip status={personalStatus}>
          <Badge variant="outline" className={cn("h-6 gap-1.5 px-2 text-[11px] font-semibold", statusMeta.className)}>
            <StatusIcon className="h-3 w-3" />
            {statusMeta.label}
          </Badge>
        </PriorityStatusTooltip>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          disabled={isBusy}
          onClick={() => onPromote(priority)}
        >
          <Inbox className="h-3.5 w-3.5" />
          Move to queue
        </Button>
      </div>
    </div>
  );
}

const MyOneThing = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentWeek = useMemo(() => getCurrentWeek(), []);
  const [holdingOpen, setHoldingOpen] = useState(true);
  const [detailPriority, setDetailPriority] = useState<Priority | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [promoteUserPid, setPromoteUserPid] = useState<string | null>(null);

  const handleOpenDetail = (priority: Priority) => {
    setDetailPriority(priority);
    setIsDetailOpen(true);
  };

  const prioritiesQuery = useMyPriorities(user?.pid);

  const allPriorities = useMemo(() => prioritiesQuery.data?.data ?? [], [prioritiesQuery.data?.data]);

  const focusPriority = useMemo(
    () => allPriorities.find((priority) => getPersonalStatus(priority, user?.pid) === "focus") ?? null,
    [allPriorities, user?.pid],
  );

  const allQueuePriorities = useMemo(
    () => allPriorities.filter((priority) => getPersonalStatus(priority, user?.pid) === "queue"),
    [allPriorities, user?.pid],
  );

  const queuePriorities = allQueuePriorities; // render the full queue, no cap
  const remainingQueueCount = 0; // all queue items now shown inline

  // Holding-state priorities the user owns (on-hold/waiting/blocked), promotable into the queue.
  const onHoldPriorities = useMemo(
    () =>
      allPriorities.filter((priority) => {
        if (!getUserAssignment(priority, user?.pid)) return false;
        return HOLDING_STATUSES.includes(getPersonalStatus(priority, user?.pid));
      }),
    [allPriorities, user?.pid],
  );

  const recentReportsQuery = useQuery({
    queryKey: ["my-onething", "recent-reports", user?.pid],
    queryFn: () =>
      reportService.getReports(1, RECENT_REPORT_LIMIT, {
        userPid: user?.pid,
      }),
    enabled: Boolean(user?.pid),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({
      priority,
      status,
    }: {
      priority: Priority;
      status: PriorityStatus;
    }) => {
      if (!user?.pid) {
        throw new Error("Missing current user");
      }
      return priorityService.updateOwnAssignment(priority.pid, { status });
    },
    onSuccess: (_data, { status }) => {
      queryClient.invalidateQueries({ queryKey: ["my-onething"] });
      queryClient.invalidateQueries({ queryKey: ["priorities"] });
      toast({
        title: "OneThing updated",
        description:
          status === "done"
            ? "Marked as done."
            : "Your OneThing has been updated.",
      });
      // Completing the Focus empties the slot, but which Queue item comes next is the user's call —
      // never pick one for them. The prompt runs after the done write has committed, so the promote
      // it offers is no longer counted against the focus limit alongside the row just released.
      if (status === "done" && user?.pid && allQueuePriorities.length > 0) {
        setPromoteUserPid(user.pid);
      }
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ["my-onething"] });
      // Checked before the limit copy: they name the one thing that unblocks the user, while the
      // limit message would just restate the rules. The authz codes are reachable here even though
      // this endpoint is own-row only — the row can have been dropped by the DRI since the page
      // loaded, and a bare code would surface as "Please try again" on a call that can never succeed.
      const focusMessage = getFocusUpdateRequiredMessage(error, "release") ?? getPriorityAuthzMessage(error);
      const limitMessage = focusMessage ?? getAssignmentLimitMessage(error);
      toast({
        title: limitMessage ? "Cannot update status" : "Could not update OneThing",
        description: limitMessage ?? "Please try again or report the update through chat.",
        variant: "destructive",
      });
    },
  });

  const swapFocusMutation = useMutation({
    mutationFn: (pid: string) => priorityService.swapFocus(pid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-onething"] });
      queryClient.invalidateQueries({ queryKey: ["priorities"] });
      toast({ title: "Focus updated", description: "Your OneThing has been swapped." });
    },
    onError: (error) => {
      // The status-update rule is checked first: it names a concrete next action, while the
      // focus-limit copy would just restate the limits without saying what to do.
      const updateMessage = getFocusUpdateRequiredMessage(error, "swap") ?? getPriorityAuthzMessage(error);
      const limitMessage = updateMessage ?? getAssignmentLimitMessage(error);
      toast({
        title: limitMessage ? "Cannot set focus" : "Could not set focus",
        description: limitMessage ?? "Please try again.",
        variant: "destructive",
      });
    },
  });

  const queueBusy = swapFocusMutation.isPending;

  const handleSetFocus = (priority: Priority) => {
    if (swapFocusMutation.isPending) return;
    swapFocusMutation.mutate(priority.pid);
  };

  const handlePromoteToQueue = (priority: Priority) => {
    if (updateStatusMutation.isPending) return;
    updateStatusMutation.mutate({ priority, status: "queue" });
  };

  const latestReport = useMemo(
    () => (recentReportsQuery.data?.data ?? []).find((r) => r.priority?.pid === focusPriority?.pid),
    [recentReportsQuery.data?.data, focusPriority?.pid],
  );
  const focusStatus = getPersonalStatus(focusPriority, user?.pid);
  const focusStatusMeta = getPriorityStatusMeta(focusStatus);
  const FocusStatusIcon = focusStatusMeta.icon;
  const confidence = confidenceForStatus(focusStatus);
  const ConfidenceIcon = confidence.Icon;
  const reportDateKeys = useMemo(() => {
    return new Set((recentReportsQuery.data?.data ?? []).map((report) => getDateKey(getReportDate(report))));
  }, [recentReportsQuery.data?.data]);
  const reportDays = useMemo(() => getLastReportDays(), []);
  const todayKey = getDateKey(startOfLocalDay(new Date()));
  const dailyMomentum = reportDays.map((day) => ({
    ...day,
    hasReport: reportDateKeys.has(day.key),
    isToday: day.key === todayKey,
  }));
  const daysElapsedThisWeek = new Date().getDay() || 7;
  const reportDaysThisWeek = new Set(
    (recentReportsQuery.data?.data ?? [])
      .filter((report) => {
        const reportWeek = getISOWeek(getReportDate(report));
        return reportWeek.week === currentWeek.week && reportWeek.year === currentWeek.year;
      })
      .map((report) => getDateKey(getReportDate(report))),
  ).size;
  const displayStreak = reportDaysThisWeek;
  const completion = Math.round((reportDaysThisWeek / daysElapsedThisWeek) * 100);


  return (
    <PageShell>
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-background to-secondary/5 border border-primary/10 p-8 shadow-sm">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Flame className="w-64 h-64 -mt-16 -mr-16" />
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl bg-primary/20 text-primary">
                <Flame className="h-6 w-6" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight">My OneThing</h1>
            </div>
            <p className="text-muted-foreground">
              {user?.fullName ? `${user.fullName}'s weekly focus` : "Your weekly focus"}
              {focusPriority ? ` · ${focusStatusMeta?.label ?? ""}` : ""}
            </p>
          </div>
          <Badge variant="outline" className="shrink-0 h-8 rounded-full bg-card px-4 text-sm font-semibold border-primary/20">
            Week {currentWeek.week} · {currentWeek.year}
          </Badge>
        </div>
      </div>

      <SectionLabel>This week</SectionLabel>
      {prioritiesQuery.isLoading ? (
        <LoadingCard />
      ) : prioritiesQuery.isError ? (
        <Card className="rounded-lg border-destructive/30 bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-5 text-sm text-destructive">
            <AlertCircle className="h-5 w-5 shrink-0" />
            Could not load your OneThing right now.
          </CardContent>
        </Card>
      ) : focusPriority ? (
        <Card
          onClick={() => handleOpenDetail(focusPriority)}
          className="cursor-pointer overflow-hidden rounded-lg border-border/70 bg-card shadow-sm transition-shadow hover:shadow-md"
        >
          <CardContent className="p-0">
            <div className="border-b border-border/70 p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Zap className="h-3.5 w-3.5 text-violet-500" />
                    Your OneThing
                    <span className="rounded-md border border-border bg-muted/50 px-2 py-0.5 font-mono text-[11px] normal-case tracking-normal text-foreground">
                      {focusPriority.code}
                    </span>
                  </div>
                  <h2 className="text-2xl font-semibold leading-tight text-foreground">{focusPriority.name}</h2>
                  {focusPriority.description ? (
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">{focusPriority.description}</p>
                  ) : null}
                  <div className="mt-4">
                    <PriorityMetaBadges priority={focusPriority} userPid={user?.pid} />
                  </div>
                </div>
                <PriorityStatusTooltip status={focusStatus} className="shrink-0">
                  <div className="flex shrink-0 items-center gap-2 rounded-lg border bg-muted/35 px-3 py-2 text-sm font-medium text-muted-foreground">
                    <FocusStatusIcon className="h-4 w-4" />
                    {focusStatusMeta.label}
                  </div>
                </PriorityStatusTooltip>
              </div>
            </div>

            <div className="border-b border-border/70 p-4" onClick={(e) => e.stopPropagation()}>
              <Button
                className="gap-2"
                disabled={focusStatus === "done" || updateStatusMutation.isPending}
                onClick={() => updateStatusMutation.mutate({ priority: focusPriority, status: "done" })}
              >
                {updateStatusMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {focusStatus === "done" ? "Done" : "Mark done"}
              </Button>
            </div>

            <div className="grid gap-4 p-5 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Latest update {latestReport ? `- ${getRelativeReportDate(latestReport)}` : ""}
                </div>
                <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground">
                  {recentReportsQuery.isLoading
                    ? "Loading latest update..."
                    : latestReport?.content
                      ? `"${latestReport.content}"`
                      : "No update has been reported for this OneThing yet."}
                </p>
              </div>
              <div
                className={cn(
                  "inline-flex w-fit items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold",
                  confidence.className,
                )}
              >
                <ConfidenceIcon className="h-4 w-4" />
                {confidence.label}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-lg border-border/70">
          <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold">No OneThing assigned this week</div>
              <p className="mt-1 text-sm text-muted-foreground">Pick a focus priority from your queue to get started.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <SectionLabel>Queue</SectionLabel>
      <Card className="rounded-lg border-border/70">
        <CardContent className="p-4">
          {prioritiesQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 rounded-md" />
              <Skeleton className="h-10 rounded-md" />
              <Skeleton className="h-10 rounded-md" />
            </div>
          ) : queuePriorities.length > 0 ? (
            <>
              {queuePriorities.map((priority, index) => (
                <QueueRow
                  key={priority.pid}
                  priority={priority}
                  index={index}
                  userPid={user?.pid}
                  onSetFocus={handleSetFocus}
                  onOpen={handleOpenDetail}
                  isBusy={queueBusy}
                />
              ))}
              <div className="mt-2 flex items-center justify-between border-t border-border/50 pt-2">
                {remainingQueueCount > 0 ? (
                  <span className="text-xs text-muted-foreground pl-1">
                    +{remainingQueueCount} more in queue
                  </span>
                ) : <span />}
                <Button variant="ghost" size="sm" className="gap-1 text-xs text-muted-foreground" onClick={() => navigate("/priorities")}>
                  View all
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between gap-3 py-2 text-sm text-muted-foreground">
              <span>Your queue is clear for this week.</span>
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate("/priorities")}>
                Priorities
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {onHoldPriorities.length > 0 ? (
        <>
          <SectionLabel>On hold / Blocked</SectionLabel>
          <Card className="rounded-lg border-border/70">
            <Collapsible open={holdingOpen} onOpenChange={setHoldingOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 p-4 text-left"
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
                    <span className="text-sm font-medium text-foreground">
                      {onHoldPriorities.length} on hold / blocked
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Not in your queue — pull one back when you're ready
                    </span>
                  </div>
                  {holdingOpen ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border-t border-border/60 px-4 pb-2">
                  {onHoldPriorities.map((priority) => (
                    <CandidateRow
                      key={priority.pid}
                      priority={priority}
                      userPid={user?.pid}
                      onPromote={handlePromoteToQueue}
                      onOpen={handleOpenDetail}
                      isBusy={updateStatusMutation.isPending}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        </>
      ) : null}

      <SectionLabel>Momentum</SectionLabel>
      <Card className="rounded-lg border-border/70">
        <CardContent className="grid gap-5 p-5 md:grid-cols-[12rem_1fr] md:items-center">
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="text-4xl font-bold leading-none">{displayStreak}</div>
            <div className="mt-1 text-sm text-muted-foreground">days reported</div>
            <div className="mt-4 flex items-center gap-2 text-xs font-medium text-emerald-700">
              <TrendingUp className="h-4 w-4" />
              {completion}% reported
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between gap-3 text-sm">
              <span className="font-medium">This week reports</span>
              <span className="text-muted-foreground">
                {reportDaysThisWeek}/{daysElapsedThisWeek} days
              </span>
            </div>
            <Progress value={completion} className="h-2 bg-muted" />
            <div className="mt-4 flex items-center gap-2">
              {dailyMomentum.map((day) => (
                <div
                  key={day.key}
                  title={`${day.label}, ${day.key}`}
                  className={cn(
                    "h-3.5 flex-1 rounded-full border",
                    day.hasReport && "border-emerald-300 bg-emerald-500",
                    !day.hasReport && "border-border bg-muted",
                    day.isToday && "ring-2 ring-primary/25",
                  )}
                />
              ))}
            </div>
            <div className="mt-3 text-xs text-muted-foreground">Last {LAST_REPORT_DAYS} days based on submitted reports.</div>
          </div>
        </CardContent>
      </Card>

      <PriorityDetailDialog
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        priority={detailPriority}
      />

      {promoteUserPid ? (
        <PromoteQueueToFocusDialog
          open={Boolean(promoteUserPid)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setPromoteUserPid(null);
          }}
          userPid={promoteUserPid}
          reason="done"
        />
      ) : null}
    </PageShell>
  );
};

export default MyOneThing;
